import { CommerceRepository } from "../repository";
import { Article, ArticleFilter, Cart, CartLine, Money, PagedResult, Product, ProductFilter, ProductVariant } from "../types";
import { products } from "../../data/products";
import { collections } from "../../data/collections";
import { articles } from "../../data/articles";

const NETWORK_DELAY_MS = 250;
const CARTS_STORAGE_KEY = "maison_noma_mock_carts";

function delay<T>(value: T): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(value), NETWORK_DELAY_MS));
}

function toCents(money: Money): number {
    return Math.round(parseFloat(money.amount) * 100);
}

function money(cents: number): Money {
    return { amount: (cents / 100).toFixed(2), currencyCode: "EUR" };
}

function loadCarts(): Record<string, Cart> {
    try {
        const raw = localStorage.getItem(CARTS_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function saveCarts(carts: Record<string, Cart>) {
    localStorage.setItem(CARTS_STORAGE_KEY, JSON.stringify(carts));
}

let cartsStore: Record<string, Cart> = loadCarts();

function findVariant(variantId: string): { product: Product; variant: ProductVariant } | null {
    for (const product of products) {
        const variant = product.variants.find((v) => v.id === variantId);
        if (variant) {
            return { product, variant };
        }
    }
    return null;
}

function recomputeCart(cart: Cart): Cart {
    const totalQuantity = cart.lines.reduce((sum, line) => sum + line.quantity, 0);
    const subtotalCents = cart.lines.reduce((sum, line) => sum + toCents(line.cost.totalAmount), 0);
    return {
        ...cart,
        totalQuantity,
        cost: {
            subtotalAmount: money(subtotalCents),
            totalAmount: money(subtotalCents),
        },
    };
}

function matchesProductFilter(product: Product, filter?: ProductFilter): boolean {
    if (!filter) return true;
    if (filter.category && !product.tags.includes(filter.category)) return false;
    if (filter.productType && product.productType !== filter.productType) return false;
    if (filter.collectionHandle && !product.tags.includes(filter.collectionHandle)) return false;
    if (filter.query) {
        const q = filter.query.toLowerCase();
        if (!product.title.toLowerCase().includes(q) && !product.description.toLowerCase().includes(q)) return false;
    }
    const minPrice = parseFloat(product.priceRange.minVariantPrice.amount);
    if (filter.priceMin !== undefined && minPrice < filter.priceMin) return false;
    if (filter.priceMax !== undefined && minPrice > filter.priceMax) return false;
    return true;
}

function sortProducts(items: Product[], sortKey?: ProductFilter["sortKey"]): Product[] {
    const sorted = [...items];
    switch (sortKey) {
        case "PRICE_ASC":
            return sorted.sort((a, b) => parseFloat(a.priceRange.minVariantPrice.amount) - parseFloat(b.priceRange.minVariantPrice.amount));
        case "PRICE_DESC":
            return sorted.sort((a, b) => parseFloat(b.priceRange.minVariantPrice.amount) - parseFloat(a.priceRange.minVariantPrice.amount));
        case "TITLE":
            return sorted.sort((a, b) => a.title.localeCompare(b.title));
        case "CREATED":
        default:
            return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
}

function matchesArticleFilter(article: Article, filter?: ArticleFilter): boolean {
    if (!filter) return true;
    if (filter.category && !article.tags.includes(filter.category)) return false;
    if (filter.query) {
        const q = filter.query.toLowerCase();
        if (!article.title.toLowerCase().includes(q) && !article.excerpt.toLowerCase().includes(q)) return false;
    }
    return true;
}

export const mockRepository: CommerceRepository = {
    async getProducts(filter) {
        const filtered = sortProducts(products.filter((p) => matchesProductFilter(p, filter)), filter?.sortKey);
        const first = filter?.first ?? filtered.length;
        const items = filtered.slice(0, first);
        const result: PagedResult<Product> = {
            items,
            hasNextPage: first < filtered.length,
            endCursor: items.length > 0 ? items[items.length - 1].id : null,
        };
        return delay(result);
    },

    async getProductByHandle(handle) {
        const product = products.find((p) => p.handle === handle) ?? null;
        return delay(product);
    },

    async getRelatedProducts(handle, first = 4) {
        const product = products.find((p) => p.handle === handle);
        if (!product) return delay([]);
        const related = products
            .filter((p) => p.handle !== handle && (p.productType === product.productType || p.tags.some((t) => product.tags.includes(t))))
            .slice(0, first);
        return delay(related);
    },

    async getProductTypes(category) {
        const scoped = category ? products.filter((p) => p.tags.includes(category)) : products;
        const types = Array.from(new Set(scoped.map((p) => p.productType)));
        return delay(types);
    },

    async getCollections() {
        return delay(collections);
    },

    async getCollectionByHandle(handle) {
        const collection = collections.find((c) => c.handle === handle) ?? null;
        return delay(collection);
    },

    async getArticles(filter) {
        const sorted = [...articles]
            .filter((a) => matchesArticleFilter(a, filter))
            .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
        const first = filter?.first ?? sorted.length;
        const items = sorted.slice(0, first);
        const result: PagedResult<Article> = {
            items,
            hasNextPage: first < sorted.length,
            endCursor: items.length > 0 ? items[items.length - 1].id : null,
        };
        return delay(result);
    },

    async getArticleByHandle(handle) {
        const article = articles.find((a) => a.handle === handle) ?? null;
        return delay(article);
    },

    async getArticleCategories() {
        const categories = Array.from(new Set(articles.flatMap((a) => a.tags)));
        return delay(categories);
    },

    async getRelatedArticles(handle, first = 3) {
        const article = articles.find((a) => a.handle === handle);
        if (!article) return delay([]);
        const related = articles
            .filter((a) => a.handle !== handle && a.tags.some((t) => article.tags.includes(t)))
            .slice(0, first);
        return delay(related);
    },

    async createCart() {
        const id = `gid://shopify/Cart/${crypto.randomUUID()}`;
        const cart: Cart = {
            id,
            checkoutUrl: `/checkout-demo?cart=${encodeURIComponent(id)}`,
            totalQuantity: 0,
            cost: { subtotalAmount: money(0), totalAmount: money(0) },
            lines: [],
        };
        cartsStore = { ...cartsStore, [id]: cart };
        saveCarts(cartsStore);
        return delay(cart);
    },

    async getCart(cartId) {
        return delay(cartsStore[cartId] ?? null);
    },

    async addCartLine(cartId, variantId, quantity) {
        const cart = cartsStore[cartId];
        if (!cart) throw new Error(`Cart ${cartId} not found`);
        const found = findVariant(variantId);
        if (!found) throw new Error(`Variant ${variantId} not found`);

        const existingLine = cart.lines.find((l) => l.merchandise.id === variantId);
        let lines: CartLine[];
        if (existingLine) {
            lines = cart.lines.map((l) =>
                l.id === existingLine.id
                    ? {
                          ...l,
                          quantity: l.quantity + quantity,
                          cost: { totalAmount: money(toCents(found.variant.price) * (l.quantity + quantity)) },
                      }
                    : l
            );
        } else {
            const newLine: CartLine = {
                id: `gid://shopify/CartLine/${crypto.randomUUID()}`,
                quantity,
                merchandise: {
                    id: found.variant.id,
                    title: found.variant.title,
                    image: found.variant.image ?? found.product.featuredImage,
                    price: found.variant.price,
                    product: { id: found.product.id, handle: found.product.handle, title: found.product.title },
                },
                cost: { totalAmount: money(toCents(found.variant.price) * quantity) },
            };
            lines = [...cart.lines, newLine];
        }

        const updatedCart = recomputeCart({ ...cart, lines });
        cartsStore = { ...cartsStore, [cartId]: updatedCart };
        saveCarts(cartsStore);
        return delay(updatedCart);
    },

    async updateCartLine(cartId, lineId, quantity) {
        const cart = cartsStore[cartId];
        if (!cart) throw new Error(`Cart ${cartId} not found`);

        let lines: CartLine[];
        if (quantity <= 0) {
            lines = cart.lines.filter((l) => l.id !== lineId);
        } else {
            lines = cart.lines.map((l) =>
                l.id === lineId
                    ? { ...l, quantity, cost: { totalAmount: money(toCents(l.merchandise.price) * quantity) } }
                    : l
            );
        }

        const updatedCart = recomputeCart({ ...cart, lines });
        cartsStore = { ...cartsStore, [cartId]: updatedCart };
        saveCarts(cartsStore);
        return delay(updatedCart);
    },

    async removeCartLine(cartId, lineId) {
        const cart = cartsStore[cartId];
        if (!cart) throw new Error(`Cart ${cartId} not found`);
        const lines = cart.lines.filter((l) => l.id !== lineId);
        const updatedCart = recomputeCart({ ...cart, lines });
        cartsStore = { ...cartsStore, [cartId]: updatedCart };
        saveCarts(cartsStore);
        return delay(updatedCart);
    },
};
